import * as React from 'react';
import {
  Sheet,
  SheetStat,
  TriCheckbox,
  WorkspaceButton,
  WorkspaceNotice,
} from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  conflictReason,
  metadataEntries,
  useApplyMetadataTidyMutation,
  useMetadataTidyPreviewQuery,
  type MetadataTidyCandidate,
  type MetadataTidyResult,
} from '@/lib/api/requirement-library';
import { plural } from './library-logic';

/**
 * Tidy appended metadata (curators, from edit mode).
 *
 * Some library requirements carry register fields at the end of their
 * wording ("… Tag: Massachusetts. Live: July 2027. Basis: R-08. Proof:
 * Demo."), left there by the Massachusetts import of 2026-10-22. Matched to
 * a client's RTM, that trailing text ended up in the client's requirements.
 *
 * The sheet lists each requirement of the module whose wording ends in
 * those labels, with its wording before and after and the fields that move.
 * Every one is ticked except where the clean wording would repeat another
 * requirement of the module. Confirming writes the ticked ones into the
 * module's draft; the curator reviews and publishes it as usual, so the
 * published versions stay as they were.
 */

export interface MetadataTidySheetProps {
  moduleId: string;
  moduleName: string;
  /** The screen's draft write queue, as for every other draft write. */
  runWrite: <T>(moduleId: string, fn: (baseRevision: number) => Promise<T>) => Promise<T>;
  onClose: () => void;
  onTidied: (result: MetadataTidyResult) => void;
}

/** The toast after a tidy, from the server's own result. */
export function metadataTidyMessage(result: MetadataTidyResult, moduleName: string): string {
  return `Metadata moved out of ${plural(result.tidied, 'requirement')} in the draft of ${moduleName}. Review the draft, then publish it.`;
}

export function MetadataTidySheet({
  moduleId,
  moduleName,
  runWrite,
  onClose,
  onTidied,
}: MetadataTidySheetProps) {
  const previewQuery = useMetadataTidyPreviewQuery(moduleId);
  const applyMutation = useApplyMetadataTidyMutation();
  const [unticked, setUnticked] = React.useState<ReadonlySet<string>>(new Set());
  const [error, setError] = React.useState<string | null>(null);

  const candidates = React.useMemo(() => previewQuery.data?.candidates ?? [], [previewQuery.data]);
  const selectable = candidates.filter((c) => c.blocked === null);
  const selected = selectable.filter((c) => !unticked.has(c.item_id));
  const blockedCount = candidates.length - selectable.length;
  const allState =
    selected.length === 0 ? 'off' : selected.length === selectable.length ? 'on' : 'mixed';

  const toggle = (itemId: string, on: boolean) =>
    setUnticked((prev) => {
      const next = new Set(prev);
      if (on) next.delete(itemId);
      else next.add(itemId);
      return next;
    });

  const confirm = () => {
    setError(null);
    const itemIds = selected.map((c) => c.item_id);
    runWrite(moduleId, (baseRevision) =>
      applyMutation.mutateAsync({ moduleId, itemIds, baseRevision }),
    ).then(
      (result) => {
        onTidied(result);
        onClose();
      },
      (err: unknown) => {
        const reason = conflictReason(err);
        if (reason === 'stale_revision' || reason === 'nothing_to_tidy') {
          void previewQuery.refetch();
          setError(
            'The draft was changed by another curator. The list has been reloaded: check it, then confirm again.',
          );
          return;
        }
        setError(err instanceof Error && err.message ? err.message : 'The draft was not changed.');
      },
    );
  };

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      closeOnScrimClick={false}
      widthClassName="w-[min(960px,94vw)]"
      title="Tidy appended metadata"
      description={`Into the draft of ${moduleName}. Nothing changes for engagements until you publish the module.`}
      summary={
        previewQuery.data ? (
          <>
            <SheetStat value={selected.length} label="selected" />
            <SheetStat value={candidates.length} label="with appended metadata" />
            {blockedCount > 0 ? <SheetStat value={blockedCount} label="left out" /> : null}
          </>
        ) : undefined
      }
      actions={
        <>
          <WorkspaceButton size="md" onClick={onClose}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            variant="primary"
            disabled={selected.length === 0 || applyMutation.isPending}
            onClick={confirm}
          >
            {applyMutation.isPending
              ? 'Moving…'
              : selected.length > 0
                ? `Move metadata out of ${plural(selected.length, 'requirement')}`
                : 'Nothing selected'}
          </WorkspaceButton>
        </>
      }
    >
      <div className="min-w-0 flex-1 overflow-y-auto p-5">
        {previewQuery.isPending ? (
          <div className="flex items-center gap-2 text-body-sm text-ink-500">
            <Spinner /> Looking for appended metadata…
          </div>
        ) : previewQuery.isError ? (
          <ErrorMessage error={previewQuery.error} />
        ) : candidates.length === 0 ? (
          <p className="text-body-sm text-ink-500">
            No requirement in {moduleName} ends in labelled metadata. There is nothing to tidy.
          </p>
        ) : (
          <>
            <p className="mb-3 max-w-[72ch] text-body-sm text-ink-700">
              {candidates.length.toLocaleString()} of the{' '}
              {plural(previewQuery.data.scanned, 'requirement')} in this module{' '}
              {candidates.length === 1 ? 'ends' : 'end'} in labelled metadata (Tag, Live, Basis,
              Proof, Pending). For each one ticked, the metadata moves out of the wording into the
              requirement&rsquo;s own fields, so only the requirement reaches a client&rsquo;s RTM.
              Untick any you want to leave as they are.
            </p>
            {error ? (
              <WorkspaceNotice tone="critical" className="mb-3">
                {error}
              </WorkspaceNotice>
            ) : null}
            <div className="mb-1.5 flex items-center gap-2.5 px-3 text-meta text-ink-500">
              <TriCheckbox
                state={allState}
                aria-label="Select all"
                disabled={selectable.length === 0}
                onChange={(on) =>
                  setUnticked(on ? new Set() : new Set(selectable.map((c) => c.item_id)))
                }
              />
              Select all
            </div>
            <ul aria-label="Requirements to tidy" className="rounded-card border border-ink-100">
              {candidates.map((c) => (
                <TidyRow
                  key={c.item_id}
                  candidate={c}
                  checked={c.blocked === null && !unticked.has(c.item_id)}
                  onToggle={(on) => toggle(c.item_id, on)}
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </Sheet>
  );
}

function TidyRow({
  candidate: c,
  checked,
  onToggle,
}: {
  candidate: MetadataTidyCandidate;
  checked: boolean;
  onToggle: (on: boolean) => void;
}) {
  const fields = metadataEntries(c.extracted);
  return (
    <li className="grid grid-cols-[16px_104px_1fr] gap-3 border-b border-ink-100 px-3 py-2.5 text-body-sm last:border-b-0">
      <TriCheckbox
        className="mt-0.5"
        state={checked ? 'on' : 'off'}
        aria-label={`Tidy ${c.number}`}
        disabled={c.blocked !== null}
        onChange={onToggle}
      />
      <span>
        <span className="block font-mono text-meta font-medium text-ink-700">{c.number}</span>
        <span className="block text-meta text-ink-500">{c.subcategory}</span>
      </span>
      <span className="min-w-0">
        <span className="block whitespace-pre-line text-ink-500 line-through">{c.before}</span>
        <span className="mt-1 block text-ink-900">{c.after}</span>
        <span className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-meta text-ink-500">
          {fields.map((f) => (
            <span key={f.field}>
              <span className="font-medium text-ink-700">{f.label}</span> {f.value}
            </span>
          ))}
        </span>
        {c.source === 'draft' ? (
          <span className="mt-1 block text-meta text-ink-500">
            Tidies the pending change in this draft.
          </span>
        ) : null}
        {c.blocked ? (
          <span className="mt-1 block text-meta text-posture-at-risk">
            Left out: without its metadata it would have the same text as {c.blocked.number}. Change
            or retire one of them first.
          </span>
        ) : null}
      </span>
    </li>
  );
}
